import React, { useState, useRef, useEffect, useCallback, useImperativeHandle, useContext, createContext } from "react";

import { useReactToPrint } from "react-to-print";
import * as pdfjsLib from "pdfjs-dist";
import workerSrc from "pdfjs-dist/build/pdf.worker.min.mjs?url";
pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc;

import { useAuthStore } from "@user/store/auth_store";

import "./print.scoped.scss";

export interface PrintHandle {
  print: () => Promise<void>;
}

type PdfRenderTask = () => Promise<void>;

const PrintContext = createContext<{
  register: (task: PdfRenderTask) => () => void;
} | null>(null);

function Print({ children, ref }: {
  children: React.ReactNode;
  ref: React.Ref<PrintHandle>;
}) {
  const printContentRef = useRef<HTMLDivElement>(null);
  const reactToPrint = useReactToPrint({ contentRef: printContentRef });
  const pdfRenderTasksRef = useRef<Set<PdfRenderTask>>(new Set());

  const register = useCallback((task: PdfRenderTask) => {
    pdfRenderTasksRef.current.add(task);
    return () => { pdfRenderTasksRef.current.delete(task); };
  }, []);

  useImperativeHandle(ref, () => ({
    print: async () => {
      await Promise.all([...pdfRenderTasksRef.current].map((task) => task()));
      reactToPrint();
    },
  }));

  return (
    <PrintContext.Provider value={{ register }}>
      <div ref={printContentRef} className="print-target">
        {children}
      </div>
    </PrintContext.Provider>
  );
}

const PrintPdf = ({ pdfUrls, width, height }: {
  pdfUrls: string[];
  width: number;
  height?: number;
}) => {
  const pdfContentRef = useRef<HTMLDivElement>(null);
  const ctx = useContext(PrintContext);

  const renderPdf = useCallback(async () => {
    const container = pdfContentRef.current;
    if (!container) return;
    container.innerHTML = ""; // 렌더링 시 초기화

    for (const pdfUrl of pdfUrls) {
      const pdf = await pdfjsLib.getDocument({
        url: pdfUrl,
        httpHeaders: {
          "x-session-id": useAuthStore.getState().sessionId
        },
        withCredentials: true,
      }).promise;

      for (let pageNo=1; pageNo<=pdf.numPages; ++pageNo) {
        const DPI = 300;
        const page = await pdf.getPage(pageNo);
        const targetWidthPx = Math.round((width / 25.4) * DPI);
        const scale = targetWidthPx / page.getViewport({ scale: 1 }).width;
        const viewport = page.getViewport({ scale });

        const canvas = document.createElement("canvas");
        canvas.width = targetWidthPx;
        if (height) {
          canvas.height = Math.round((height / 25.4) * DPI) - 1;
        } else {
          canvas.height = Math.floor(viewport.height) - 1;;
        }
        canvas.className = "pdf-page";
        const scopeAttr = Array.from(container.attributes).find(attr => attr.name.startsWith("data-"));
        if (scopeAttr) {
          canvas.setAttribute(scopeAttr.name, "");
        }

        const context = canvas.getContext("2d")!;
        await page.render({ canvasContext: context, viewport, canvas }).promise;
        container.appendChild(canvas);
      }
    };
  }, [pdfUrls]);

  useEffect(() => {
    if (!ctx) return;
    return ctx.register(renderPdf);
  }, [ctx, renderPdf]);

  return (
    <div className="print-pdf">
      <div ref={pdfContentRef}>
      </div>
    </div>
  );
};

const MemoPrint = React.memo(Print) as React.MemoExoticComponent<typeof Print> & {
  Pdf: typeof PrintPdf;
};
MemoPrint.Pdf = PrintPdf;

export default MemoPrint;
